export type AppNotificationKind =
  | "validation_done"
  | "share_sent"
  | "invite_accepted"
  | "document_uploaded"
  | "passport_generated"
  | "share_downloaded"
  | "audit";

export type AppNotification = {
  id: string;
  kind: AppNotificationKind;
  title: string;
  detail?: string;
  caseId?: string;
  createdAt: string;
  read: boolean;
  source?: "local" | "server";
};

const STORAGE_KEY = "dds-notifications-v1";
const MAX_ITEMS = 40;

type Listener = () => void;
const listeners = new Set<Listener>();

function readAll(): AppNotification[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as AppNotification[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(items: AppNotification[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
  listeners.forEach((l) => l());
}

export function listNotifications(): AppNotification[] {
  return readAll();
}

export function unreadNotificationCount(): number {
  return readAll().filter((n) => !n.read).length;
}

export function pushNotification(
  input: Omit<AppNotification, "id" | "createdAt" | "read"> & { id?: string; createdAt?: string }
): AppNotification {
  const item: AppNotification = {
    id: input.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    kind: input.kind,
    title: input.title,
    detail: input.detail,
    caseId: input.caseId,
    createdAt: input.createdAt || new Date().toISOString(),
    read: false,
    source: input.source || "local",
  };
  const next = [item, ...readAll().filter((n) => n.id !== item.id)];
  writeAll(next);
  return item;
}

/** Merge audit-like events without duplicating by id. */
export function ingestAuditNotifications(
  events: { id: string; action: string; detail: string | null; created_at: string }[],
  caseId: string,
  labelForAction: (action: string) => string
) {
  const existing = new Set(readAll().map((n) => n.id));
  const interesting = events.filter((e) =>
    /validat|share|invite|passport|accept|upload|download/i.test(e.action)
  );
  let changed = false;
  const merged = [...readAll()];
  for (const e of interesting) {
    const id = `audit-${e.id}`;
    if (existing.has(id)) continue;
    merged.unshift({
      id,
      kind: kindFromAction(e.action),
      title: labelForAction(e.action),
      detail: e.detail || undefined,
      caseId,
      createdAt: e.created_at,
      read: true,
      source: "local",
    });
    changed = true;
  }
  if (changed) writeAll(merged);
}

function kindFromAction(action: string): AppNotificationKind {
  const a = action.toLowerCase();
  if (a.includes("invite_accepted") || a === "invite_accepted") return "invite_accepted";
  if (a.includes("document_uploaded") || a.includes("upload")) return "document_uploaded";
  if (a.includes("share_downloaded") || a.includes("download")) return "share_downloaded";
  if (a.includes("passport")) return "passport_generated";
  if (a.includes("validat")) return "validation_done";
  if (a.includes("share")) return "share_sent";
  return "audit";
}

function kindFromEventType(eventType: string): AppNotificationKind {
  if (eventType.includes("invite_accepted")) return "invite_accepted";
  if (eventType.includes("document_uploaded")) return "document_uploaded";
  if (eventType.includes("share_downloaded")) return "share_downloaded";
  if (eventType.includes("passport.ready") || eventType.includes("passport.generated")) {
    return "passport_generated";
  }
  if (eventType.includes("validation")) return "validation_done";
  if (eventType.includes("passport.shared")) return "share_sent";
  return "audit";
}

/** US-507 / US-1006: merge notification-center rows into the bell feed. */
export function ingestServerNotifications(
  rows: {
    id: string;
    event_id: string;
    event_type: string;
    title: string;
    body: string | null;
    payload: string | null;
    is_read?: boolean;
    created_at: string;
  }[]
) {
  const merged = readAll();
  const byId = new Map(merged.map((n) => [n.id, n]));
  let changed = false;
  for (const row of rows) {
    const id = `server-${row.event_id || row.id}`;
    let caseId: string | undefined;
    try {
      const payload = row.payload ? (JSON.parse(row.payload) as { case_id?: string }) : {};
      caseId = payload.case_id;
    } catch {
      caseId = undefined;
    }
    const serverRead = row.is_read === true;
    const existing = byId.get(id);
    if (existing) {
      // Prefer server unread (failed sibling channels must stay unread).
      if (existing.read && !serverRead) {
        existing.read = false;
        changed = true;
      } else if (!existing.read && serverRead) {
        existing.read = true;
        changed = true;
      }
      continue;
    }
    merged.unshift({
      id,
      kind: kindFromEventType(row.event_type),
      title: row.title,
      detail: row.body || undefined,
      caseId,
      createdAt: row.created_at,
      read: serverRead,
      source: "server",
    });
    changed = true;
  }
  if (changed) {
    merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    writeAll(merged);
  }
}

export function markAllNotificationsRead() {
  writeAll(readAll().map((n) => ({ ...n, read: true })));
}

export function clearNotifications() {
  writeAll([]);
}

export function subscribeNotifications(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
